export const STAGES = ["Servant of God", "Venerable", "Blessed", "Saint"];

export const STAGE_INFO = {
  "Servant of God":
    "A person whose cause for canonization has officially begun; the Church is investigating their life and virtues.",
  Venerable:
    "The Church has recognized heroic virtue or advanced the cause significantly; not beatified or canonized yet.",
  Blessed:
    "Beatified; public veneration is permitted in certain places or communities, often local or within a religious order.",
  Saint:
    "Canonized; officially declared a 'big-S' Saint and venerated by the universal Church.",
};

// Grouped categories (use this for grouped dropdowns / sections)
export const CATEGORY_GROUPS = [
  {
    label: "Biblical & Early Church",
    options: [
      "Apostle",
      "Disciple",
      "Evangelist",
      "Archangel",
      "Prophet",
      "Church Father",
    ],
  },
  {
    label: "Witness & Teaching",
    options: ["Martyr", "Confessor", "Doctor of the Church", "Missionary", "Mystic"],
  },
  {
    label: "States of Life",
    options: [
      "Virgin",
      "Layperson",
      "Consecrated Virgin",
      "Religious",
      "Brother",
      "Sister",
      "Monk",
      "Nun",
      "Friar",
      "Hermit",
    ],
  },
  {
    label: "Holy Orders & Hierarchy",
    options: ["Deacon", "Priest", "Bishop", "Archbishop", "Cardinal", "Pope", "Seminarian"],
  },
  {
    label: "Religious Orders & Families",
    options: [
      "Jesuit",
      "Franciscan",
      "Dominican",
      "Carmelite",
      "Benedictine",
      "Augustinian",
      "Capuchin",
      "Cistercian",
      "Trappist",
      "Norbertine",
      "Carthusian",
      "Oratorian",
      "Redemptorist",
      "Salesian",
      "Vincentian",
    ],
  },
];

// Keep a flat list too (backwards compatible with existing code)
export const CATEGORY_OPTIONS = CATEGORY_GROUPS.flatMap((g) => g.options);

export const CATEGORY_INFO = {
  // Biblical & Early Church
  Apostle:
    "One of the primary witnesses sent by Christ; commonly the Twelve, and sometimes others closely commissioned, like Paul.",
  Disciple:
    "A follower of Jesus or a close early follower in the apostolic Church distinct from the Twelve apostles.",
  Evangelist:
    "Traditionally, an author of a Gospel (Matthew, Mark, Luke, John) or a major herald of the Gospel.",
  Archangel:
    "A chief angel in Scripture and tradition with a special mission (e.g., Michael, Gabriel, Raphael).",
  Prophet:
    "One who speaks God’s message with special authority; often a biblical prophet or prophetic witness.",
  "Church Father":
    "An early Christian teacher known for orthodox doctrine, holiness, and influence in the Church’s first centuries.",

  // Witness & Teaching
  Martyr:
    "One who died for the Christian faith or a closely related virtue as a witness to Christ.",
  Confessor:
    "A saint who suffered persecution for the faith without being killed; in some usages, also associated with priestly ministry of confession.",
  "Doctor of the Church":
    "A saint recognized as having made a significant contribution to Catholic theology or doctrine through teaching or writing.",
  Missionary:
    "A saint especially known for bringing the Gospel to new peoples or places through evangelization.",
  Mystic:
    "One known for profound union with God in prayer and contemplation, sometimes accompanied by recognized mystical experiences.",

  // States of Life
  Virgin:
    "A saint who lived a consecrated life of virginity and chastity dedicated to God.",
  Layperson:
    "A non-ordained member of the faithful; many saints are lay men and women known for holiness in ordinary life.",
  "Consecrated Virgin":
    "A woman consecrated to a life of perpetual virginity for Christ (often living in the world or in community) under the care of the bishop.",
  Religious:
    "A person who professes public vows (e.g., poverty, chastity, obedience) in a recognized religious institute.",
  Brother:
    "A religious man who is not ordained (or is not primarily defined by priestly ministry), living vows in community and apostolic service.",
  Sister:
    "A religious woman living vows in community, often in apostolic service; distinct from monastic 'nun' in many traditions.",
  Monk:
    "A male member of a monastic community living under a rule, centered on prayer, work, and stability.",
  Nun:
    "A female member of a monastic community living under a rule, devoted to prayer and communal monastic life.",
  Friar:
    "A member of a mendicant religious community (e.g., Franciscans, Dominicans) with an emphasis on preaching and service.",
  Hermit:
    "One who lived a life of prayer and solitude (eremitical life), typically with a recognized Christian witness of holiness.",

  // Holy Orders & Hierarchy
  Deacon:
    "An ordained minister of the Church (transitional or permanent) called especially to service, charity, and proclamation of the Gospel.",
  Priest:
    "An ordained minister who serves the Church through preaching, sacraments, and pastoral care.",
  Bishop:
    "An ordained successor of the apostles who shepherds a local Church (diocese) with teaching, sanctifying, and governing authority.",
  Archbishop:
    "A bishop with an archdiocese (often of special historical or regional significance) or with metropolitan responsibilities.",
  Cardinal:
    "A senior Church leader (usually a bishop) appointed by the Pope, often serving as an advisor and (if eligible) a papal elector.",
  Pope:
    "A bishop of Rome who served as Pope; shepherd of the universal Church in his pontificate.",
  Seminarian:
    "A man in formal formation and study preparing for possible ordination to the priesthood (or diaconate in some contexts).",

  // Religious Orders & Families
  Jesuit:
    "A member of the Society of Jesus (SJ), known for education, missionary work, and spiritual formation rooted in the Spiritual Exercises.",
  Franciscan:
    "A member of the Franciscan family, emphasizing poverty, simplicity, and love of creation in the spirit of St. Francis of Assisi.",
  Dominican:
    "A member of the Order of Preachers (OP), emphasizing preaching, study, and the defense and proclamation of the faith.",
  Carmelite:
    "A member of the Carmelite family, emphasizing contemplative prayer and spiritual life rooted in the Carmelite tradition.",
  Benedictine:
    "A monastic following the Rule of St. Benedict, emphasizing stability, communal prayer, and work ('ora et labora').",
  Augustinian:
    "A religious in the Augustinian tradition, often emphasizing community life, interior conversion, and pastoral ministry.",
  Capuchin:
    "A reform branch of the Franciscans known for simplicity, preaching, and a strong emphasis on lived poverty.",
  Cistercian:
    "A monastic in the Cistercian tradition, a reform of Benedictine monasticism emphasizing simplicity and contemplative life.",
  Trappist:
    "A member of the Order of Cistercians of the Strict Observance (Trappists), known for rigorous monastic discipline and contemplative life.",
  Norbertine:
    "A member of the Premonstratensian order (Norbertines), a canon regular community combining liturgical life with pastoral ministry.",
  Carthusian:
    "A member of the Carthusian order, known for an especially contemplative, semi-eremitical (solitary) monastic life.",
  Oratorian:
    "A member of the Oratory (e.g., Congregation of the Oratory of St. Philip Neri), priests living in community without formal vows.",
  Redemptorist:
    "A member of the Congregation of the Most Holy Redeemer, known for preaching missions and serving the poor.",
  Salesian:
    "A member of the Salesians of Don Bosco, known especially for education and ministry to young people.",
  Vincentian:
    "A member of a Vincentian family (e.g., Congregation of the Mission), emphasizing charity and service of the poor in the spirit of St. Vincent de Paul.",
};
